import * as React from 'react';
import * as _ from 'lodash';
import history from 'login-utils/history';
import { diagnostics, inputFormatters, utils } from '@blend/ui-common';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';
import { css, StyleSheet } from 'aphrodite';
import { combineStyles, fonts } from '@blend/consumer-components';

import States from 'login-constants/states';
import PaneLayoutConstants from 'login-constants/paneLayoutConstants';
import { addNewLoanToBorrower } from 'login-utils/createLoanUtils';
import * as userUtils from 'login-utils/userUtils';
import * as deploymentConfig from 'login-utils/deploymentConfig';
import { WindowWrapper } from 'login-utils/globalWrappers';
import * as featureFlags from 'login-utils/featureFlags';
import * as onboardingRoutingUtils from 'login-utils/onboardingRoutingUtils';
import { getAutoProductDescription, getAutoProductType } from 'login-utils/autoProductUtils';
import { sendClickEvent } from 'login-utils/pixelUtils';

import * as loansAPI from 'login-apis/loansAPI';
import * as loginUIAPI from 'login-apis/loginUIAPI';

import * as deploymentColors from 'login-stylesheets/deploymentColors';

import LargeIconButtonGroup from 'login-components/Common/LargeIconButtonGroup/LargeIconButtonGroup';
import IconButton from 'login-components/Common/Buttons/IconButton';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import AppLayoutContextUpdater from 'login-components/Common/AppLayoutContextUpdater/AppLayoutContextUpdater';

import HeloanSVG from 'login-components/Common/svg/HeloanSVG';
import HelocSVG from 'login-components/Common/svg/HelocSVG';
import MortgageSVG from 'login-components/Common/svg/MortgageSVG';
import AutoPurchaseSVG from 'login-components/Common/svg/AutoPurchaseSVG';
import PersonalLoanSVG from 'login-components/Common/svg/PersonalLoanSVG';
import PersonalLineOfCreditSVG from 'login-components/Common/svg/PersonalLineOfCreditSVG';
import SpecialtyVehicleSVG from 'login-components/Common/svg/SpecialtyVehicleSVG';

const styles = StyleSheet.create({
  applications: {
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '24px',
  },
  existingApplications: PaneLayoutConstants.splitContentStaticWidthMixin,
  horizontalLine: combineStyles(PaneLayoutConstants.splitContentStaticWidthMixin, {
    textAlign: 'center',
    borderBottom: `1px solid ${deploymentColors.loginBackgroundDynamicColor}`,
    lineHeight: '.1em',
    margin: '10px 0 20px',
  }),
  horizontalLineText: {
    background: deploymentColors.loginBackgroundColor,
    padding: '0 10px',
    color: deploymentColors.loginBackgroundDynamicColor,
    textTransform: 'uppercase',
    ...fonts.Roboto,
    lineHeight: '.1em',
    fontWeight: fonts.weight.bold,
    fontSize: 12,
    letterSpacing: '2px',
  },
  newApplication: combineStyles(PaneLayoutConstants.splitContentStaticWidthMixin, {
    marginTop: '20px',
  }),
  newApplicationButton: {
    height: '72px',
  },
});

const loanIconMap = {
  MORTGAGE: MortgageSVG,
  HELOC: HelocSVG,
  HELOAN: HeloanSVG,
  AUTO: AutoPurchaseSVG,
  PERSONAL_LOAN: PersonalLoanSVG,
  PERSONAL_LOC: PersonalLineOfCreditSVG,
  SPECIALTY_VEHICLE: SpecialtyVehicleSVG,
  CREDIT_CARD: SpecialtyVehicleSVG,
};

const loanTypeToDisplayName = {
  CANADA_MORTGAGE: 'Mortgage',
  MORTGAGE: 'Mortgage',
  HELOC: 'Home equity line of credit',
  HELOAN: 'Home equity loan',
  AUTO: 'Auto',
  PERSONAL_LOAN: 'Personal loan',
  PERSONAL_LOC: 'Personal line of credit',
  CREDIT_CARD: 'Credit Card',
  SPECIALTY_VEHICLE: 'Specialty vehicle',
};

interface ChooseApplicationState {
  loans: DM.Loan[];
  error?: string;
  isSubmitting: boolean;
}

export type ChooseApplicationProps = ReactRouter.RouteComponentProps<{}, {}>;

export class ChooseApplication extends React.Component<ChooseApplicationProps, ChooseApplicationState> {
  constructor(props) {
    super(props);
    this.state = {
      loans: [],
      isSubmitting: false,
    };
  }

  async componentDidMount() {
    const { location } = this.props;
    if (!userUtils.getCurrentUser()) {
      return history.push(States.LOGIN);
    }
    let loans: DM.Loan[] = [];

    try {
      loans = await loansAPI.getCurrentUser();
    } catch (err) {
      this.setState({ error: 'Failed to fetch your information, please refresh and try again' });
      return;
    }

    // istanbul ignore next
    if (loans.length === 0) {
      diagnostics.error(new Error('No loans available for ChooseApplication.'));
    }

    const selectedProductIdCookie = utils.cookieUtils.get('selectedProductId');

    // selectedProductIdCookie being set indicates that the borrower explicitly chose to switch applications
    // thus we allow them to remain at this state, instead of redirecting to their only loan
    if (_.size(loans) === 1 && !selectedProductIdCookie) {
      const loanId: string = _.get(_.first(loans), '_id', '');
      /* istanbul ignore next */
      if (!loanId) throw new Error('Invalid loan');

      utils.cookieUtils.set('selectedProductId', loanId, { path: '/' });
      WindowWrapper.location.assign(`${States.CHOOSE_APPLICATION}${location.search}`);
    } else {
      if (!featureFlags.isEnabled('archiveStatusLoanChooserUpdates20200203')) {
        return this.setState({ loans });
      }

      const unarchivedLoans = _.reject(loans, 'application.archived');
      const loansToDisplay = _.isEmpty(unarchivedLoans) ? loans : unarchivedLoans;
      this.setState({ loans: loansToDisplay });
    }
  }

  onSelect = value => {
    const { location } = this.props;
    sendClickEvent({ element_name: 'NewLoan_ApplicationSelection' });
    utils.cookieUtils.set('selectedProductId', value, { path: '/' });
    // don't await sending event for performance reasons
    loginUIAPI.emitPartyApplicationAccessEvent(value);
    WindowWrapper.location.assign(`${States.CHOOSE_APPLICATION}${location.search}`);
  };

  onCreateNewApplication = async () => {
    const supportedLoanTypesAndCategories = onboardingRoutingUtils.getSupportedLoanTypesAndCategories();
    sendClickEvent({ element_name: 'NewLoan_ApplicationChooser' });
    if (_.size(supportedLoanTypesAndCategories) > 1) {
      return history.push(States.CHOOSE_LOAN_TYPE);
    }

    const loanType: LoanType | LoanTypeCategory = _.first(supportedLoanTypesAndCategories)!;

    // Send the user directly to the consumer lending loan flows if applicable
    switch (loanType) {
      case 'AUTO':
        return history.push(States.AUTO_LOAN);
      case 'SPECIALTY_VEHICLE':
        return history.push(States.SPECIALTY_VEHICLE_CATEGORY);
      case 'PERSONAL':
        return history.push(States.PERSONAL_LOAN_TYPE);
      case 'CREDIT_CARD':
        return history.push(States.CREDIT_CARD_PRODUCT);
    }

    // Immediately create the application if there is only 1 supported loan type that is not consumer lending
    this.setState({
      error: '',
      isSubmitting: true,
    });

    try {
      await addNewLoanToBorrower({ loanType });
    } catch (e) {
      this.setState({
        error: 'Failed to create your new application, please refresh and try again',
        // Only unset on error, since we navigate away in the success case
        // and don't want the user to be able to submit again
        isSubmitting: false,
      });
      return;
    }
  };

  loanTypeToIcon(loanType) {
    return _.get(loanIconMap, loanType, MortgageSVG);
  }

  getLoanTypeTitle(loan: DM.Loan) {
    if (loan.application.loanPurposeType === 'CONSTRUCTION') return 'Construction';
    return _.get(loanTypeToDisplayName, loan.application.loanType) || loan.application.loanType;
  }

  generateLoanText(loan: DM.Loan) {
    const loanIndex =
      _.findIndex(_.filter(this.state.loans, l => this.getLoanTypeTitle(l) === this.getLoanTypeTitle(loan)), {
        _id: loan._id,
      }) + 1;
    const lenderCase = (function() {
      const identifiers: DM.LoanIdentifier[] = _.get(loan, 'application.loanIdentifiers', []);
      const lenderIdentifier = _.find(identifiers, { loanIdentifierType: 'LENDER_CASE' });
      return _.get(lenderIdentifier, 'loanIdentifier');
    })();

    return `${this.getLoanTypeTitle(loan)} #${lenderCase || loan.text || loanIndex}`;
  }

  generateLoanSubtext(loan) {
    const loanStartDate = utils.dateUtils.convertTimestampToDate(loan.createdAt);
    const firstLine = `Started on ${loanStartDate}`;
    const formattedLoanAmount = storedLoanAmount => {
      return !!storedLoanAmount ? inputFormatters.MONEY_CENTS.formatWithDollarSign(storedLoanAmount) : null;
    };

    const getSecondLineFields = (loanType: DM.LoanType) => {
      switch (loanType) {
        case 'MORTGAGE':
        case 'HELOC':
        case 'HELOAN':
        case 'CANADA_MORTGAGE':
          return _.compact([
            formattedLoanAmount(loan.application.loanAmount),
            _.get(loan.application, 'propertyInfo.address.lineText'),
          ]);
        case 'AUTO':
          const autoApplication = _.get(loan, 'Auto.autoApplication');
          const autoProductType = getAutoProductType(autoApplication);
          const autoProductDescription = getAutoProductDescription(autoProductType);
          return _.compact([formattedLoanAmount(_.get(autoApplication, 'loanAmount')), autoProductDescription]);
        default:
          // For example, student doesn't have a concept of a subject property,
          // so can define their own description later
          return [];
      }
    };

    const secondLineFields = getSecondLineFields(loan.application.loanType);
    const secondLine = _.join(secondLineFields, ' • ');

    return _.compact([firstLine, secondLine]);
  }

  render() {
    const { error, loans, isSubmitting } = this.state;
    if (error) {
      return (
        <div className={css(styles.applications)}>
          <ErrorMessage errorText={error} />
        </div>
      );
    }

    if (!_.size(loans)) return null;
    const title = 'Choose Application';

    let loanNumber = 1;
    const loanConfigs = _.map(loans, loan => ({
      value: loan._id,
      iconComponent: this.loanTypeToIcon(loan.application.loanType),
      buttonId: `it-chooseApplication${loanNumber++}`,
      text: this.generateLoanText(loan),
      subText: this.generateLoanSubtext(loan),
    }));

    const borrowerSelfServeLoanCount = _.filter(loans, loan => loan.application.leadSource === 'BORROWER');
    const showNewApplicationButton =
      featureFlags.isEnabled('applicationChooserInDropdownMenu20190109') &&
      !!deploymentConfig.get().allowSignup &&
      _.size(borrowerSelfServeLoanCount) < 10;

    return (
      <AppLayoutContextUpdater footerConfig={null} appDocumentTitle={title} appTitle={title}>
        <div data-it="ChooseApplication" className={css(styles.applications)}>
          <div data-exclude-fullstory="always" className={css(styles.existingApplications)}>
            <Field
              name={'chooseApplication'}
              configs={loanConfigs}
              onSelect={this.onSelect}
              component={LargeIconButtonGroup}
            />
          </div>
          {showNewApplicationButton && (
            <div className={css(styles.newApplication)}>
              <p className={css(styles.horizontalLine)}>
                <span className={css(styles.horizontalLineText)}>Want to apply again?</span>
              </p>
              <div className={css(styles.newApplicationButton)}>
                <IconButton
                  buttonId="it-createNewApplicationButton"
                  text="Start a new application"
                  onClick={isSubmitting ? _.noop : this.onCreateNewApplication}
                />
              </div>
            </div>
          )}
        </div>
      </AppLayoutContextUpdater>
    );
  }
}

export default reduxForm<FormProps<DataShape, ChooseApplicationProps, ReduxState>, ChooseApplicationProps, ReduxState>({
  form: 'ChooseApplication',
})(ChooseApplication);
